import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button, Form, Tabs } from 'antd';
import type { Moment } from 'moment';

import { getMissingRequiredFieldNames } from './utils/promoStateUtils';
import {
    TPO_PROMO_CANCEL_BTN,
    TPO_PROMO_SAVE_BTN,
    TPO_PROMO_TABS,
    tpoPromoTabTestId,
} from '../../config/testIds';
import { useCustomerConfig, useTpoGenConfig } from '../../context/CustomerConfigContext';
import { buildAttributesPayload } from '../../utils/entityAttributes';
import { TRANSFORMS } from '../../utils/buildPromoPayload';
import type { ApiPPG, PPGRow } from '../../types/promoMetrics';
import type { RuleOption, DimSelectFieldConfig } from '../../config/tpoGenConfig';
import { buildOptionResolvers } from '../../rules/resolvers/optionResolvers';
import { useScopeFieldStates } from '../../rules/store/selectors';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import { useEditSession } from '../EditSession/EditSessionContext';
import useGetPromoData from './hooks/queries/useGetPromoData';
import useRecommendedValues from './hooks/queries/useRecommendedValues';
import useProductAttributeFilters from './hooks/queries/useProductAttributeFilters';
import useCreatePromoMutation from './hooks/mutations/useCreatePromoMutation';
import useUpliftMutation from './hooks/mutations/useUpliftMutation';
import usePpgData from './hooks/ui/usePpgData';
import usePromoDirtyState from './hooks/ui/usePromoDirtyState';
import usePromoSave from './hooks/ui/usePromoSave';
import useUpliftTrigger from './hooks/ui/useUpliftTrigger';
import { usePromoKpiColumns } from './hooks/ui/usePromoKpiColumns';
import { cascadeTriggerFields } from './utils/applyFormCascade';
import PromoDesigner from './PromoDesigner';
import { useOptionalRuleStoreApi } from '../../rules/store/FieldRulesProvider';
import {
    TPOGEN_PROMOTION_CREATED,
    TPOGEN_PROMOTION_UPDATED,
    TPOGEN_PROMO_TYPE_CHANGED,
    TPOGEN_PRODUCTS_ADDED_TO_PROMOTION,
} from 'utils/mixpanelEventNames';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../../utils/trackTpoGenEvent';

/** Classifies which kind of field changed for `change_type` on promotion-update events. */
const classifyPromoChangeType = (
    touchedFieldNames: Set<string>,
    fieldNameMap: {
        promoType?: string;
        startDate?: string;
        endDate?: string;
        craStartDate?: string;
        craEndDate?: string;
    },
): 'scope' | 'dates' | 'name' => {
    if (fieldNameMap.promoType && touchedFieldNames.has(fieldNameMap.promoType)) return 'scope';
    const dateFieldNames = new Set(
        [fieldNameMap.startDate, fieldNameMap.endDate, fieldNameMap.craStartDate, fieldNameMap.craEndDate].filter(
            (name): name is string => !!name,
        ),
    );
    for (const fieldName of touchedFieldNames) {
        if (dateFieldNames.has(fieldName)) return 'dates';
    }
    return 'name';
};

type PromoTabProps = {
    planId: string;
    eventId: string;
    /** Per-promo-tab id (the EventsView tab key); isolates this tab's rule-engine state. */
    instanceId: string;
    promotionId?: string;
    /** Whether this promo tab is the currently active tab; drives lazy refetch after an event update. */
    isActive?: boolean;
    /** Event's current date values keyed by apiKey; carried into a new promo's (disabled) date fields. */
    eventDatesByApiKey?: Record<string, unknown>;
    onPromoSaved?: (promotionId: string) => void;
    onDirtyChange?: (isDirty: boolean) => void;
};

/** Imperative API exposed to EventsView so it can discard this tab's unsaved changes when the user navigates away without saving. */
export interface PromoTabHandle {
    reset: () => void;
}

function formatDate(val: Moment | string | undefined): string {
    if (!val) return '';
    if (typeof val === 'string') return val;
    return val.format('YYYY-MM-DD');
}

const PromoTab = forwardRef<PromoTabHandle, PromoTabProps>(({ planId, eventId, instanceId, promotionId, isActive = true, eventDatesByApiKey, onPromoSaved, onDirtyChange }, ref) => {
    const [promoForm] = Form.useForm();
    const { parsedPromoOptions } = useCustomerConfig();
    const { promoFields, formCascadeRules, entityAttributes, labels, promoDetailsFields, planFields, upliftConfig } = useTpoGenConfig();
    const [savedPromotionId, setSavedPromotionId] = useState<string | null>(promotionId ?? null);
    const [selectedPpgIds, setSelectedPpgIds] = useState<number[]>([]);
    const ppgDataRef = useRef<PPGRow[]>([]);

    const noApiFieldNames = useMemo(
        () => promoFields.filter((f) => f.apiKey === null).map((f) => f.name),
        [promoFields],
    );
    const apiSyncedFieldNames = useMemo(
        () => promoFields.filter((f) => f.responseKey !== null || f.offerMapping).map((f) => f.name),
        [promoFields],
    );
    // Offer fields (and their unit-toggle unit field) whose user edits should re-fetch uplift.
    const offerTriggerFieldNames = useMemo(
        () =>
            new Set(
                promoFields
                    .filter((f) => f.offerMapping)
                    .flatMap((f) => (f.unitToggle ? [f.name, f.unitToggle.unitField] : [f.name])),
            ),
        [promoFields],
    );
    // Form fields whose user edits should re-run the deterministic form -> metric cascade:
    // each rule's triggers + input/unit fields + the `promo:` sources of its `when`/variant
    // conditions (so a gating field change - e.g. status/tactic - also re-runs the cascade).
    const cascadeTriggerFieldNames = useMemo(() => cascadeTriggerFields(formCascadeRules), [formCascadeRules]);

    const { currentPlanDetails } = usePlanDetailsContext();
    const { canEdit } = useEditSession();

    const promoFieldNameMap = useMemo(() => {
        const find = (apiKey: string) => promoFields.find((f) => f.apiKey === apiKey)?.name;
        return {
            promoType: find(upliftConfig.promoTypeApiKey),
            startDate: find('start_date'),
            endDate: find('end_date'),
            craStartDate: find('cra_start_date'),
            craEndDate: find('cra_end_date'),
        };
    }, [promoFields, upliftConfig.promoTypeApiKey]);

    // Promo date fields (field name + apiKey) used to carry the event's dates into
    // a new promo's disabled date fields, matched to the event by apiKey.
    const promoDateFields = useMemo(
        () =>
            promoFields
                .filter((f) => f.type === 'datepicker' && f.apiKey)
                .map((f) => ({ name: f.name, apiKey: f.apiKey as string })),
        [promoFields],
    );

    const dim2Attrs: string[] = useMemo(() => {
        const customerField = planFields.find(
            (f): f is DimSelectFieldConfig & { apiKey: string } =>
                f.type === 'dimSelect' &&
                (f as DimSelectFieldConfig).dimension === upliftConfig.customerDimension &&
                'apiKey' in f &&
                !!(f as { apiKey?: string }).apiKey,
        );
        if (!customerField) return [];
        return (currentPlanDetails?.[customerField.apiKey as keyof typeof currentPlanDetails] as string[]) ?? [];
    }, [planFields, upliftConfig.customerDimension, currentPlanDetails]);

    const effectivePromotionId = promotionId ?? savedPromotionId ?? null;
    const isEditMode = !!promotionId || !!savedPromotionId;

    // Rule-engine: mirror promo-level context into the store. `hasEntityId` is
    // per-promo-instance (each tab has its own); `planYear` is plan-global.
    const ruleStore = useOptionalRuleStoreApi();
    useEffect(() => {
        if (!ruleStore) return;
        ruleStore.getState().setPromoHasEntityId(instanceId, !!effectivePromotionId);
        // TODO: derive from the event's promo order (eventPromos[0] / no saved promos) once EventsView exposes it.
        ruleStore.getState().setPromoIsFirstPromo(instanceId, false);
        ruleStore.getState().setContext({ planYear: currentPlanDetails?.plan_year });
    }, [ruleStore, instanceId, effectivePromotionId, currentPlanDetails?.plan_year]);

    // --- queries ---
    const {
        promoFormValues: existingPromoFormValues,
        ppgData: existingPpgData,
        ppgIds: existingPpgIds,
        promoMeta,
        applicableMetricKeys: promoApplicableMetricKeys,
        defaultVisibleMetricKeys: promoDefaultVisibleMetricKeys,
        isPending: isLoadingPromoData,
        isFetching: isFetchingPromoData,
        isRefetchingAfterUpdate,
    } = useGetPromoData(effectivePromotionId, isActive);

    const { options: productOptions, isPending: isLoadingProducts } = useProductAttributeFilters({ eventId });

    const rawPromoFormValues = Form.useWatch([], promoForm);
    const promoFormValues: Record<string, unknown> = useMemo(() => rawPromoFormValues ?? {}, [rawPromoFormValues]);

    // Engine-derived per-field UI state for the promo scope (drives the required-fields gate).
    const baseOptionsByTarget = useMemo(() => {
        const map: Record<string, RuleOption[]> = {};
        for (const f of promoFields) {
            if (f.options) map[`promo:${f.name}`] = f.options as RuleOption[];
        }
        return map;
    }, [promoFields]);
    const promoResolvers = useMemo(
        () => buildOptionResolvers({ parsedPromoOptions, baseOptionsByTarget }),
        [parsedPromoOptions, baseOptionsByTarget],
    );
    const promoFieldStates = useScopeFieldStates('promo', promoResolvers, instanceId);

    // Fields the rule engine has disabled hold values carried/derived from other
    // scopes (e.g. event dates today, possibly other mirrored fields later). They
    // have no form initialValue, so a bare resetFields() would blank them — track
    // their names so the create-flow reset can skip them.
    const disabledFieldNames = useMemo(
        () => new Set(promoFields.filter((f) => promoFieldStates[f.name]?.disabled).map((f) => f.name)),
        [promoFields, promoFieldStates],
    );

    // Names of promo fields the user has touched since the last save; used only to
    // classify `change_type` (name|dates|scope) on the update-analytics event.
    const [touchedPromoFieldNames, setTouchedPromoFieldNames] = useState<Set<string>>(new Set());

    const [hasFormErrors, setHasFormErrors] = useState(false);
    const validationFrameRef = useRef<number>();
    const lastHydratedFormValuesRef = useRef<Record<string, unknown> | null>(null);

    useEffect(() => {
        if (validationFrameRef.current !== undefined) {
            cancelAnimationFrame(validationFrameRef.current);
        }
        validationFrameRef.current = requestAnimationFrame(() => {
            promoForm
                .validateFields({ validateOnly: true })
                .then(() => setHasFormErrors(false))
                .catch(() => setHasFormErrors(true));
        });
    }, [promoForm, rawPromoFormValues]);

    // Single required-fields gate: drives the Add Products / KPI affordances, the
    // recommended-values fetch, and the metrics-table empty state.
    const missingRequiredFieldNames = useMemo(
        () => getMissingRequiredFieldNames(promoFieldStates, promoFormValues),
        [promoFieldStates, promoFormValues],
    );
    const isPromoMetaComplete = missingRequiredFieldNames.length === 0;
    const missingRequiredLabels = useMemo(() => {
        const labelByName = new Map(promoFields.map((f) => [f.name, f.label.replace(':', '')]));
        return missingRequiredFieldNames.map((name) => labelByName.get(name) ?? name);
    }, [missingRequiredFieldNames, promoFields]);

    const isAddProductsDisabled = !isPromoMetaComplete;
    const promotionType = (promoFieldNameMap.promoType ? (promoFormValues[promoFieldNameMap.promoType] as string) : undefined) ?? '';
    const promoStartDate = formatDate(promoFieldNameMap.startDate ? promoFormValues[promoFieldNameMap.startDate] as Moment | string | undefined : undefined);
    const promoEndDate = formatDate(promoFieldNameMap.endDate ? promoFormValues[promoFieldNameMap.endDate] as Moment | string | undefined : undefined);
    const craStartDate = formatDate(promoFieldNameMap.craStartDate ? promoFormValues[promoFieldNameMap.craStartDate] as Moment | string | undefined : undefined);
    const craEndDate = formatDate(promoFieldNameMap.craEndDate ? promoFormValues[promoFieldNameMap.craEndDate] as Moment | string | undefined : undefined);

    // Dynamic, config-driven attributes object (promotion_type, tactic, and any
    // future promotion-level attribute) for the recommended-values request.
    const promoAttributes = useMemo(
        () => buildAttributesPayload(entityAttributes, promoFields, promoFormValues, 'promotion', TRANSFORMS),
        [entityAttributes, promoFields, promoFormValues],
    );

    const {
        ppgData: fetchedPpgData,
        applicableMetricKeys: recommendedApplicableMetricKeys,
        defaultVisibleMetricKeys: recommendedDefaultVisibleMetricKeys,
        isLoading: isLoadingRecommendedValues,
        isFetchingNextPage: isLoadingNextPage,
        hasNextPage: hasMoreRecommendedValues,
        fetchNextPage: fetchNextRecommendedPage,
    } = useRecommendedValues({
        planId,
        eventId,
        attributes: promoAttributes,
        startDate: promoStartDate,
        endDate: promoEndDate,
        craStartDate,
        craEndDate,
        ppgIds: selectedPpgIds,
        enabled: isPromoMetaComplete && selectedPpgIds.length > 0,
    });

    // Metric visibility is server-computed per cross-section. The recommended-values response
    // reflects the live form (products just added / edited), so it wins; the get-promo-by-id
    // response covers viewing a saved promo before any products are added. `null` from both
    // => neither endpoint supplied it and PromoMetricsSection falls back to the rule engine.
    const applicableMetricKeys = recommendedApplicableMetricKeys ?? promoApplicableMetricKeys;
    const defaultVisibleMetricKeys =
        recommendedDefaultVisibleMetricKeys ?? promoDefaultVisibleMetricKeys;

    // --- ui hooks ---
    const {
        ppgData,
        hasProducts,
        baseline,
        onMetricChange,
        applyFormCascade,
        onAddProducts,
        resetEditableSpendsToZero,
        resetPpgToExisting,
        clearPpgState,
        snapshotBaseline,
    } = usePpgData({ existingPpgData, existingPpgIds, fetchedPpgData, formCascadeRules, promoFormValues });

    ppgDataRef.current = ppgData;

    // Per-tab KPI (visible-column) customizations. Owned here so they survive the
    // first-save spinner remount of PromoMetricsSection, and are dropped when this
    // tab unmounts (close/reopen resets to defaults).
    const kpiColumns = usePromoKpiColumns();

    // --- uplift ---
    // Mutation for POST /model_uplift (network layer only)
    const { mutateAsync: upliftMutateAsync, isPending: isUpliftPending } = useUpliftMutation();

    // Orchestrates validation, payload building, and response cascade for uplift
    const { triggerUplift, triggerUpliftBeforeSave, triggerUpliftAuto, triggerUpliftDebounced, isUpliftApplicable } =
        useUpliftTrigger({
            ppgData,
            promoFormValues,
            promoType: promotionType || undefined,
            dim2Attrs,
            planId,
            eventId,
            promotionId: effectivePromotionId,
            onMetricChange,
            mutateAsync: upliftMutateAsync,
            isPending: isUpliftPending,
            fieldStates: promoFieldStates,
        });

    const handleAddProducts = useCallback(
        (ppgIds: number[]) => {
            const { newIds } = onAddProducts(ppgIds, existingPpgIds);
            setSelectedPpgIds(newIds);
            if (newIds.length > 0) {
                trackTpoGenEvent(TPOGEN_PRODUCTS_ADDED_TO_PROMOTION, TPOGEN_PAGE_NAMES.PLAN_DETAIL_PROMO, {
                    count: newIds.length,
                    promo_id: effectivePromotionId ?? undefined,
                    selection_method: 'manual',
                });
            }
        },
        [onAddProducts, existingPpgIds, effectivePromotionId],
    );

    const isFormHydrated = lastHydratedFormValuesRef.current === existingPromoFormValues;

    const { isDirty, canSave } = usePromoDirtyState({

        isEditMode,
        ppgData,
        baseline,
        promoFormValues,
        existingPromoFormValues,
        existingPpgIds,
        hasProducts,
        isLoadingRecommendedValues,
        isFetchingPromoData,
        isFormHydrated,
        hasFormErrors,
        isUpliftPending,
        fieldStates: promoFieldStates,
    });

    const isPromoSaveEnabled = canSave && canEdit;

    // --- mutation ---
    const handleSaveSuccess = useCallback(
        (
            data: { promotion_id?: string; data?: { promotion_id?: string } },
            variables?: { promotion_id: string | null },
        ) => {
            const promoId = data?.promotion_id ?? data?.data?.promotion_id ?? variables?.promotion_id ?? undefined;
            const wasEdit = variables?.promotion_id != null;
            if (wasEdit) {
                trackTpoGenEvent(TPOGEN_PROMOTION_UPDATED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_PROMO, {
                    event_id: eventId,
                    promotion_id: promoId,
                    change_type: classifyPromoChangeType(touchedPromoFieldNames, promoFieldNameMap),
                });
            } else {
                trackTpoGenEvent(TPOGEN_PROMOTION_CREATED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_PROMO, {
                    event_id: eventId,
                    promotion_id: promoId,
                    change_type: 'created',
                    source: 'plans',
                });
            }
            if (promoId) {
                setSavedPromotionId(promoId);
                onPromoSaved?.(promoId);
            }
            promoForm.resetFields(noApiFieldNames);
            snapshotBaseline();
            setSelectedPpgIds([]);
            setTouchedPromoFieldNames(new Set());
        },
        [promoForm, snapshotBaseline, onPromoSaved, noApiFieldNames, eventId, touchedPromoFieldNames, promoFieldNameMap],
    );

    const { mutate: createPromo, isPending: isPromoMutationPending } = useCreatePromoMutation({
        onSuccess: handleSaveSuccess,
    });

    const { save, reset: resetPromo } = usePromoSave({
        promoForm,
        ppgData,
        baseline,
        planId,
        eventId,
        savedPromotionId,
        promoMeta,
        existingPromoFormValues,
        existingPpgData,
        isEditMode,
        createPromo,
        resetPpgToExisting,
        clearPpgState,
        triggerUpliftBeforeSave,
        disabledFieldNames,
    });

    const reset = useCallback(() => {
        resetPromo();
        setSelectedPpgIds([]);
    }, [resetPromo]);

    useImperativeHandle(ref, () => ({ reset }), [reset]);

    const handleFormValuesChange = useCallback(
        (changedValues: Record<string, unknown>) => {
            const changedKeys = Object.keys(changedValues);
            setTouchedPromoFieldNames((prev) => {
                const next = new Set(prev);
                for (const key of changedKeys) next.add(key);
                return next;
            });
            if (promoFieldNameMap.promoType && changedKeys.includes(promoFieldNameMap.promoType)) {
                trackTpoGenEvent(TPOGEN_PROMO_TYPE_CHANGED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_PROMO, {
                    from_type: promotionType || undefined,
                    to_type: changedValues[promoFieldNameMap.promoType],
                });
                resetEditableSpendsToZero();
                // Re-trigger recommended values API so KPI columns update for the new promo type.
                // Products already in the table were loaded from the saved promo (selectedPpgIds is
                // empty), so we must explicitly populate it with all current ppg IDs.
                const currentPpgIds = ppgDataRef.current.map((p) => p.ppg_id);
                if (currentPpgIds.length > 0) {
                    setSelectedPpgIds(currentPpgIds);
                }
            }
            // Deterministic form -> metric cascade. Read fresh values from the form
            // instance (not Form.useWatch which lags one render) because the rule
            // engine may have already reset related fields via setFieldsValue within
            // the same synchronous call (e.g. offerType change resets userInput to undefined).
            if (changedKeys.some((key) => cascadeTriggerFieldNames.has(key))) {
                const freshValues = promoForm.getFieldsValue(true);
                applyFormCascade({ ...freshValues, ...changedValues });
            }
            // Re-fetch uplift only on user-initiated offer edits. Driving this off onValuesChange
            // (rather than watching derived form values) avoids spurious calls when the form is
            // populated programmatically on load/refetch — e.g. right after a save.
            if (changedKeys.some((key) => offerTriggerFieldNames.has(key))) {
                triggerUpliftDebounced();
            }
        },
        [
            resetEditableSpendsToZero,
            triggerUpliftDebounced,
            offerTriggerFieldNames,
            cascadeTriggerFieldNames,
            applyFormCascade,
            promoForm,
            promoFieldNameMap.promoType,
            promotionType,
        ],
    );

    // --- effects ---
    useEffect(() => {
        if (existingPromoFormValues) {
            promoForm.resetFields(apiSyncedFieldNames);
            promoForm.setFieldsValue(existingPromoFormValues);
            lastHydratedFormValuesRef.current = existingPromoFormValues;
            if (ruleStore) {
                ruleStore.getState().setPromoValues(instanceId, existingPromoFormValues, { source: 'hydrate' });
            }
        }
    }, [existingPromoFormValues, promoForm, ruleStore, instanceId, apiSyncedFieldNames]);

    // For a NEW promo (no saved promotion yet), the disabled date fields mirror the
    // event's current form dates, matched by apiKey. For a saved promo the hydration
    // effect above (API values) owns the dates, so this is a no-op then. `source:
    // 'hydrate'` keeps the mirror from spuriously triggering uplift/cascade.
    useEffect(() => {
        if (effectivePromotionId || !eventDatesByApiKey) return;
        const patch: Record<string, unknown> = {};
        for (const { name, apiKey } of promoDateFields) {
            const value = eventDatesByApiKey[apiKey];
            if (value !== undefined) patch[name] = value;
        }
        if (Object.keys(patch).length === 0) return;
        promoForm.setFieldsValue(patch);
        if (ruleStore) {
            ruleStore.getState().setPromoValues(instanceId, patch, { source: 'hydrate' });
        }
    }, [eventDatesByApiKey, effectivePromotionId, promoDateFields, promoForm, ruleStore, instanceId]);

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    // Auto-trigger uplift when new recommended values are fetched (PPG/date/tactic change).
    // `fetchedPpgData` arrives one commit before `ppgData` (derived in usePpgData via async
    // setPpgData) reflects it, so we (1) record the new fetch + the ppgData seen at that moment,
    // then (2) fire only once ppgData has actually committed the new rows. The "consumed fetch"
    // marker is advanced only when we fire, which avoids the previous state-commit race and
    // guarantees the uplift payload is built from the freshly fetched metrics.
    const lastUpliftedFetchRef = useRef<ApiPPG[] | null>(null);
    const pendingFetchRef = useRef<{ fetch: ApiPPG[]; ppgAtDetect: PPGRow[] } | null>(null);

    useEffect(() => {
        if (
            fetchedPpgData.length > 0 &&
            fetchedPpgData !== lastUpliftedFetchRef.current &&
            pendingFetchRef.current?.fetch !== fetchedPpgData
        ) {
            pendingFetchRef.current = { fetch: fetchedPpgData, ppgAtDetect: ppgData };
        }

        const pending = pendingFetchRef.current;
        if (
            pending &&
            ppgData !== pending.ppgAtDetect &&
            ppgData.length > 0 &&
            isUpliftApplicable &&
            !isUpliftPending
        ) {
            lastUpliftedFetchRef.current = pending.fetch;
            pendingFetchRef.current = null;
            triggerUpliftAuto();
        }
    }, [fetchedPpgData, ppgData, isUpliftApplicable, isUpliftPending, triggerUpliftAuto]);

    return (
        <div data-testid={TPO_PROMO_TABS}>
            <div className="flex flex-row justify-between items-center">
                <p className="text-base font-medium">
                    {effectivePromotionId ? labels.promos.editPromo : labels.promos.addNewPromo}
                </p>
                <div className="flex flex-row gap-x-2">
                    <Button
                        type="primary"
                        autoFocus={true}
                        onClick={save}
                        disabled={!isPromoSaveEnabled}
                        loading={isPromoMutationPending}
                        data-testid={TPO_PROMO_SAVE_BTN}
                        data-state-disabled={!isPromoSaveEnabled ? 'true' : undefined}
                    >
                        {effectivePromotionId ? labels.promos.updatePromo : labels.promos.savePromo}
                    </Button>
                    <Button type="default" onClick={reset} data-testid={TPO_PROMO_CANCEL_BTN}>
                        {'Reset'}
                    </Button>
                </div>
            </div>
            <Tabs
                items={[
                    {
                        key: '1',
                        label: <span data-testid={tpoPromoTabTestId('designer')}>{'Designer'}</span>,
                        children: (
                            <PromoDesigner
                                promoForm={promoForm}
                                instanceId={instanceId}
                                ppgData={ppgData}
                                onMetricChange={onMetricChange}
                                onFormValuesChange={handleFormValuesChange}
                                onAddProducts={handleAddProducts}
                                isAddProductsDisabled={isAddProductsDisabled}
                                isLoadingRecommendedValues={isLoadingRecommendedValues}
                                isLoadingNextPage={isLoadingNextPage}
                                hasMoreRecommendedValues={hasMoreRecommendedValues}
                                fetchNextRecommendedPage={fetchNextRecommendedPage}
                                hasProducts={hasProducts}
                                existingProductIds={existingPpgIds}
                                eventId={eventId}
                                isLoadingPromoData={isLoadingPromoData || isRefetchingAfterUpdate}
                                promotionId={effectivePromotionId}
                                parsedOptions={parsedPromoOptions}
                                productOptions={productOptions}
                                isLoadingProducts={isLoadingProducts}
                                onRefreshUplift={triggerUplift}
                                isUpliftPending={isUpliftPending}
                                isUpliftApplicable={isUpliftApplicable}
                                isPromoMetaComplete={isPromoMetaComplete}
                                missingRequiredLabels={missingRequiredLabels}
                                applicableMetricKeys={applicableMetricKeys}
                                defaultVisibleMetricKeys={defaultVisibleMetricKeys}
                                kpiColumns={kpiColumns}
                            />
                        ),
                    },
                    {
                        key: '2',
                        label: (
                            <span data-testid={tpoPromoTabTestId('insights')} data-state-disabled="true">
                                {'Insights'}
                            </span>
                        ),
                        children: (
                            <p className="text-xl text-[#9F9FA9]" data-testid={tpoPromoTabTestId('insights_empty')}>
                                {'No insights available'}
                            </p>
                        ),
                        disabled: true,
                    },
                ]}
            />
        </div>
    );
});

PromoTab.displayName = 'PromoTab';
export default PromoTab;
